import { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl-next';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { messages as commonMessages } from '../../common/messages';
import {
	type ActionCallback,
	PageModalType,
	type RotationDetail,
	RotationType,
	ShiftFrequency,
	TimingType,
} from '../../common/types';
import { endpoints as editEndpoints } from '../../services/endpoints/edit';
import { getScheduleTimeTemplatesResource } from '../../services/resources';
import { formatting } from '../../utils/date';
import { reorderItems } from '../../utils/list';
import { useActions as useEditActions } from '../edit-store';
import {
	useInitialRotation,
	useOrderRotationLoading,
	useRecipientUpdate,
} from '../edit-store/helpers';
import { type RecipientUpdate, RotationDetailVariant } from '../edit-store/state/types';
import { useActions as usePageActions } from '../page-store';
import { useActions as useScheduleActions } from '../schedule-store';
import { fromRotationDetailToRotationPayload } from '../schedule-store/actions/utils';
import { useRotation, useTimelineRotations } from '../schedule-store/helpers';

import { messages } from './messages';
import { type FormFields } from './types';
import {
	fromGetRotationResponseToRecipientUpdate,
	fromGetRotationResponseToRotationDetail,
	fromRotationDetailToRotationForm,
	fromRotationFormToRotationDetail,
	generateDefaultStart,
	generateName,
} from './utils';

const DEBOUNCE_TIME = 500;

const useRotationOptions = () => {
	const intlApi = useIntl();

	const { data: scheduleTimeTemplates } = useResource(getScheduleTimeTemplatesResource);

	const timingOptions = useMemo(
		() => [
			{
				name: TimingType.Everday,
				value: TimingType.Everday,
				label: intlApi.formatMessage(messages.forEveryday),
			},
			{
				name: TimingType.Customday,
				value: TimingType.Customday,
				label: intlApi.formatMessage(messages.customizeDays),
			},
		],
		[intlApi],
	);

	const templateOptions = useMemo(
		() =>
			scheduleTimeTemplates?.timeTemplates.map(
				({ id: value, name: label, restrictions, timingType }) => ({
					value,
					label,
					restrictions,
					timingType,
				}),
			) ?? [],
		[scheduleTimeTemplates?.timeTemplates],
	);

	const shiftFrequencyOptions = useMemo(
		() => [
			{
				name: ShiftFrequency.Daily,
				value: ShiftFrequency.Daily,
				label: intlApi.formatMessage(messages.daily),
			},
			{
				name: ShiftFrequency.Weekly,
				value: ShiftFrequency.Weekly,
				label: intlApi.formatMessage(messages.weekly),
			},
			{
				name: ShiftFrequency.Custom,
				value: ShiftFrequency.Custom,
				label: intlApi.formatMessage(messages.custom),
			},
		],
		[intlApi],
	);

	const shiftTypeOptions = useMemo(
		() => [
			{
				name: RotationType.HOURLY,
				value: RotationType.HOURLY,
				label: intlApi.formatMessage(messages.hours),
			},
			{
				name: RotationType.DAILY,
				value: RotationType.DAILY,
				label: intlApi.formatMessage(messages.days),
			},
			{
				name: RotationType.WEEKLY,
				value: RotationType.WEEKLY,
				label: intlApi.formatMessage(messages.weeks),
			},
		],
		[intlApi],
	);

	return {
		timingOptions,
		templateOptions,
		shiftTypeOptions,
		shiftFrequencyOptions,
	};
};

const useInitialValues = () => {
	const { templateOptions, shiftTypeOptions, shiftFrequencyOptions } = useRotationOptions();

	const [rotation] = useInitialRotation();
	const [recipientUpdate] = useRecipientUpdate();

	const rotationId = useMemo(() => rotation?.id, [rotation?.id]);

	const initialValues = useMemo(() => {
		if (!rotation) {
			return undefined;
		}

		// TODO: Get timezone offset from get rotation response
		return fromRotationDetailToRotationForm({
			base: rotation,
			shiftFrequencyOptions,
			shiftTypeOptions,
			templateOptions,
		});
	}, [rotation, shiftFrequencyOptions, shiftTypeOptions, templateOptions]);

	return [rotationId, initialValues, recipientUpdate] as [
		string | undefined,
		FormFields | undefined,
		RecipientUpdate | undefined,
	];
};

const useRotationUpdate = (rotationId?: string) => {
	// NOTE: do not use rotationId in current (it might be undefined)
	const [current, { setRotationAction }] = useRotation();

	/** @description Runs only if values are different than current previewed */
	const updateRotation = useCallback(
		(values: FormFields) => {
			const incoming = fromRotationFormToRotationDetail({
				base: values,
				rotationId,
			});

			if (!isEqual(current, incoming)) {
				setRotationAction(incoming);
			}
		},
		[current, rotationId, setRotationAction],
	);

	/** @description Runs only if values are different than current previewed */
	const updateRotationDebounced = useMemo(
		() => debounce((values: FormFields) => updateRotation(values), DEBOUNCE_TIME),
		[updateRotation],
	);

	return {
		updateRotation,
		updateRotationDebounced,
	};
};

// REFACTOR: consider renaming useDeleteRotationService like hooks to useDeleteRotation
const useDeleteRotationService = (scheduleId: string, rotationId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const { closePageModal } = usePageActions();
	const { getTimelineAction } = useScheduleActions();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const defaultCatch = useDefaultCatch();

	const deleteRotation = useCallback(async () => {
		setIsLoading(true);
		try {
			await editEndpoints.deleteRotation({ scheduleId, rotationId });
		} catch (error) {
			defaultCatch<void>(
				{
					id: 'useRotationOrder-deleteRotation',
					httpErrorHandler: (err) => {
						if (err.message) {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: intlApi.formatMessage(commonMessages.error),
							});
						} else {
							flagApi.showFlag({
								...errorToastProps(),
								title: intlApi.formatMessage(messages.deleteRotationErrorTitle),
								isAutoDismiss: true,
							});
						}
					},
				},
				error,
			);
		}

		try {
			await getTimelineAction();
		} finally {
			setIsLoading(false);
			closePageModal(PageModalType.DeleteRotation);
		}
	}, [closePageModal, defaultCatch, flagApi, getTimelineAction, intlApi, rotationId, scheduleId]);

	return { isLoading, deleteRotation };
};

const useRotationOrder = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const [rotations, { getTimelineAction }] = useTimelineRotations();
	const [isLoading, { setOrderRotationLoading }] = useOrderRotationLoading();
	const [internalRotations, setInternalRotations] = useState(rotations);
	const defaultCatch = useDefaultCatch();

	useEffect(() => {
		if (rotations) {
			// filtered because when goBack invoked timeline data still has canceled data
			setInternalRotations(rotations?.filter(({ id }) => Boolean(id)));
		}
	}, [rotations]);

	const orderRotation = useCallback(
		async (sourceIndex: number, destinationIndex: number) => {
			setOrderRotationLoading(true);

			try {
				const rotationId = rotations![sourceIndex]!.id;
				let after: string;

				if (destinationIndex === 0) {
					after = '';
				} else {
					if (destinationIndex > sourceIndex) {
						after = rotations![destinationIndex].id;
					} else {
						after = rotations![destinationIndex - 1].id;
					}
				}

				setInternalRotations(reorderItems(rotations!, sourceIndex, destinationIndex));

				await editEndpoints.orderRotation({
					scheduleId,
					rotationId,
					after,
				});

				getTimelineAction();
			} catch (error) {
				setInternalRotations(rotations);
				defaultCatch<void>(
					{
						id: 'useRotationOrder-orderRotation',
						httpErrorHandler: (err) => {
							if (err.message) {
								flagApi.showFlag({
									...errorToastProps(),
									description: err.message,
									title: intlApi.formatMessage(commonMessages.error),
								});
							} else {
								flagApi.showFlag({
									...errorToastProps(),
									title: intlApi.formatMessage(messages.orderRotationErrorTitle),
								});
							}
						},
					},
					error,
				);
			} finally {
				setOrderRotationLoading(false);
			}
		},
		[
			defaultCatch,
			flagApi,
			getTimelineAction,
			intlApi,
			rotations,
			scheduleId,
			setOrderRotationLoading,
		],
	);

	return { rotations: internalRotations, isLoading, orderRotation };
};

const useRotationActions = (scheduleId: string, rotationId?: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const { goBack } = useEditActions();
	const defaultCatch = useDefaultCatch();

	const add = useCallback(
		async (fields: FormFields, actionCallback?: ActionCallback) => {
			try {
				await editEndpoints.addRotation({
					scheduleId,
					...fromRotationDetailToRotationPayload(
						fromRotationFormToRotationDetail({
							base: fields,
							rotationId,
						}),
					),
				});
				flagApi.showFlag({
					...successToastProps(),
					title: intlApi.formatMessage(messages.addRotationSuccessTitle),
				});
				goBack();
				actionCallback?.onSuccess?.();
			} catch (error) {
				defaultCatch<void>(
					{
						id: 'useRotationActions-add',
						httpErrorHandler: (err) => {
							if (err.message) {
								flagApi.showFlag({
									...errorToastProps(),
									description: err.message,
									title: intlApi.formatMessage(commonMessages.error),
								});
							} else {
								flagApi.showFlag({
									...errorToastProps(),
									title: intlApi.formatMessage(messages.addRotationErrorTitle),
									isAutoDismiss: true,
								});
							}
						},
					},
					error,
				);
				actionCallback?.onError?.();
			}
		},
		[defaultCatch, flagApi, goBack, intlApi, rotationId, scheduleId],
	);

	const edit = useCallback(
		async (fields: FormFields, actionCallback?: ActionCallback) => {
			try {
				await editEndpoints.editRotation({
					scheduleId,
					...(fromRotationDetailToRotationPayload(
						fromRotationFormToRotationDetail({
							base: fields,
							rotationId,
						}),
					) as Required<ReturnType<typeof fromRotationDetailToRotationPayload>>),
				});
				flagApi.showFlag({
					...successToastProps(),
					title: intlApi.formatMessage(messages.editRotationSuccessTitle),
				});
				goBack();
				actionCallback?.onSuccess?.();
			} catch (error) {
				defaultCatch<void>(
					{
						id: 'useRotationActions-edit',
						httpErrorHandler: (err) => {
							if (err.message) {
								flagApi.showFlag({
									...errorToastProps(),
									description: err.message,
									title: intlApi.formatMessage(commonMessages.error),
								});
							} else {
								flagApi.showFlag({
									...errorToastProps(),
									title: intlApi.formatMessage(messages.editRotationErrorTitle),
									isAutoDismiss: true,
								});
							}
						},
					},
					error,
				);
				actionCallback?.onError?.();
			}
		},
		[defaultCatch, flagApi, goBack, intlApi, rotationId, scheduleId],
	);

	return { add, edit };
};

const useRotationFlows = (scheduleId: string) => {
	const flagApi = useFlags();
	const intlApi = useIntl();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { goToDetail } = useEditActions();
	const [rotations] = useTimelineRotations();
	const { data: timeTemplateResponse } = useResource(getScheduleTimeTemplatesResource);
	const defaultCatch = useDefaultCatch();

	const clone = useCallback(
		async (rotationId: string) => {
			// TODO: use translations
			const name = generateName(rotations ?? []);

			setIsLoading(true);

			try {
				const rotation = await editEndpoints.getRotation({
					scheduleId,
					rotationId,
				});

				const detail = fromGetRotationResponseToRotationDetail(
					rotation,
					timeTemplateResponse!.timeTemplates,
				);

				const recipientUpdate = fromGetRotationResponseToRecipientUpdate(rotation);

				detail.id = undefined;
				detail.name = name;

				goToDetail({
					rotation: detail,
					variant: RotationDetailVariant.Clone,
					recipientUpdate,
				});
			} catch (error) {
				defaultCatch<void>(
					{
						id: 'useRotationFlows-clone',
						httpErrorHandler: (err) => {
							if (err.message) {
								flagApi.showFlag({
									...errorToastProps(),
									description: err.message,
									title: intlApi.formatMessage(commonMessages.error),
								});
							} else {
								flagApi.showFlag({
									...errorToastProps(),
									title: intlApi.formatMessage(messages.getRotationErrorTitle),
									actions: [
										{
											content: intlApi.formatMessage(commonMessages.tryAgain),
											onClick: () => clone(rotationId),
										},
									],
								});
							}
						},
					},
					error,
				);
			} finally {
				setIsLoading(false);
			}
		},
		[rotations, scheduleId, timeTemplateResponse, goToDetail, defaultCatch, flagApi, intlApi],
	);

	const edit = useCallback(
		async (rotationId: string) => {
			setIsLoading(true);

			try {
				const rotation = await editEndpoints.getRotation({
					scheduleId,
					rotationId,
				});

				const detail = fromGetRotationResponseToRotationDetail(
					rotation,
					timeTemplateResponse!.timeTemplates,
				);

				const recipientUpdate = fromGetRotationResponseToRecipientUpdate(rotation);

				goToDetail({
					rotation: detail,
					variant: RotationDetailVariant.Edit,
					recipientUpdate,
				});
			} catch (error) {
				defaultCatch<void>(
					{
						id: 'useRotationFlows-edit',
						httpErrorHandler: (err) => {
							if (err.message) {
								flagApi.showFlag({
									...errorToastProps(),
									description: err.message,
									title: intlApi.formatMessage(commonMessages.error),
								});
							} else {
								flagApi.showFlag({
									...errorToastProps(),
									title: intlApi.formatMessage(messages.getRotationErrorTitle),
									actions: [
										{
											content: intlApi.formatMessage(commonMessages.tryAgain),
											onClick: () => edit(rotationId),
										},
									],
								});
							}
						},
					},
					error,
				);
			} finally {
				setIsLoading(false);
			}
		},
		[scheduleId, timeTemplateResponse, goToDetail, defaultCatch, flagApi, intlApi],
	);

	const add = useCallback(() => {
		// TODO: use translations
		const name = generateName(rotations ?? []);

		const firstTemplate = timeTemplateResponse!.timeTemplates[0];

		const start = generateDefaultStart();

		const detail: RotationDetail = {
			name,
			responders: [],
			rotationPeriod: 1,
			rotationType: RotationType.WEEKLY,
			startTimeRaw: formatting.toDateHourMinute(start),
			restrictions: firstTemplate.restrictions,
			timeTemplateId: firstTemplate.id,
			timingType: firstTemplate.timingType,
		};

		goToDetail({
			rotation: detail,
			variant: RotationDetailVariant.Add,
		});
	}, [rotations, timeTemplateResponse, goToDetail]);

	return { clone, add, edit, isLoading };
};

export {
	useRotationFlows,
	useDeleteRotationService,
	useRotationOrder,
	useRotationActions,
	useRotationUpdate,
	useRotationOptions,
	useInitialValues,
};
